import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DataTable from '../common/DataTable';
import CompactFilterPanel from '../common/CompactFilterPanel';
import ImportDialog from '../common/ImportDialog';
import { Company, CompanyTableColumn, CompaniesResponse, FilterCriteria } from '../../types/company';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';
import { useEditMode } from '../../hooks/useEditMode';
import { EditModeConfig } from '../../types/editMode';
import {
  EditModeControls,
  DeleteConfirmationDialog,
  RefreshNotificationDialog,
  EditWarningDialog,
} from '../common/editMode';
import { useEditWarning } from '../../hooks/useEditWarning';
import { usePermissions } from '../../hooks/usePermissions';
import { useImport } from '../../context/ImportContext';

const CompanyView: React.FC = () => {
  const { canEdit, canDownload } = usePermissions();
  const navigate = useNavigate();
  const [listName, setListName] = useState('');
  const [isListNameSaved, setIsListNameSaved] = useState(false);
  const [selectedAllInQuery, setSelectedAllInQuery] = useState(false);
  const [outreachErrorDialog, setOutreachErrorDialog] = useState({ open: false, title: '', message: '' });
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const importManager = useImport();

  // Auto-minimize import if user navigates away while import dialog is open
  useEffect(() => {
    return () => {
      // Cleanup: if import is running and dialog is open (not minimized), minimize it
      if (showImportDialog && 
          importManager.state.progress && 
          !importManager.state.isMinimized &&
          (importManager.state.progress.status === 'processing' || 
           importManager.state.progress.status === 'queued')) {
        importManager.minimize();
      }
    };
  }, [showImportDialog, importManager]);
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 100,
    total: 0
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteria[]>([]);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({
    column: 'id',
    direction: 'ASC'
  });

  // Memoize allColumns to prevent unnecessary re-renders in useMemo dependencies
  const allColumns: CompanyTableColumn[] = useMemo(() => [
    // Ordered according to user specification
    { id: 'website', label: 'Website', minWidth: 150, type: 'text' },
    { id: 'clean_name', label: 'DBA', minWidth: 200, type: 'text' },
    { id: 'name', label: 'Company Name', minWidth: 200, type: 'text' },
    { id: 'note', label: 'Notes', minWidth: 200, type: 'text' },
    { id: 'description', label: 'Description', minWidth: 300, type: 'text' },
    { id: 'unsubscribe', label: 'Unsubscribe', minWidth: 120, type: 'number' },
    { id: 'in_mailer', label: 'Inmailer', minWidth: 120, type: 'number' },
    { id: 'state_name', label: 'State', minWidth: 100, type: 'text' },
    { id: 'location', label: 'Location', minWidth: 150, type: 'text' },
    { id: 'country_name', label: 'Country', minWidth: 100, type: 'text' },
    { id: 'company_address', label: 'Company Address', minWidth: 200, type: 'text' },
    { id: 'snov_id', label: 'Snov ID', minWidth: 120, type: 'text' },
    { id: 'industry_name', label: 'Industry Tag', minWidth: 150, type: 'text' },
    { id: 'industry_tag_two_name', label: 'Industry Tag Two', minWidth: 150, type: 'text' },
    { id: 'industry_tag_three_name', label: 'Industry Tag Three', minWidth: 150, type: 'text' },
    { id: 'industry_tag_four_name', label: 'Industry Tag Four', minWidth: 150, type: 'text' },
    { id: 'deliverable', label: 'Del', minWidth: 55, type: 'number' },
    { id: 'risky', label: 'Ris', minWidth: 55, type: 'number' },
    { id: 'undeliverable', label: 'Und', minWidth: 55, type: 'number' },
    { id: 'bounce', label: 'Bou', minWidth: 55, type: 'number' },
    { id: 'Linkedin', label: 'LI', minWidth: 60, type: 'text' },
    { id: 'employees_onlinkedin', label: 'Employees on LinkedIn', minWidth: 150, type: 'text' },
    { id: 'linkedin_id', label: 'LinkedIn ID', minWidth: 150, type: 'text' },
    { id: 'twitter', label: 'TW', minWidth: 60, type: 'text' },
    { id: 'Facebook', label: 'FB', minWidth: 60, type: 'text' },
    { id: 'ig', label: 'IG', minWidth: 60, type: 'text' },
    { id: 'Youtube', label: 'YT', minWidth: 60, type: 'text' },
    { id: 'license', label: 'License', minWidth: 120, type: 'text' },
    { id: 'phone', label: 'Phone', minWidth: 150, type: 'text' },
    { id: 'unknown', label: 'Unknown', minWidth: 100, type: 'number' },
    { id: 'not_verified', label: 'Not Verified', minWidth: 120, type: 'number' },
    { id: 'suspicious', label: 'Suspicious', minWidth: 120, type: 'text' },
    { id: 'last_ran_snov', label: 'Snov Last Ran', minWidth: 150, type: 'text' },
    { id: 'last_ran_gmo', label: 'GMO Last Ran', minWidth: 150, type: 'text' },
    { id: 'last_ran_reoon', label: 'Reoon Last Ran', minWidth: 150, type: 'text' },
    // ID columns (shown in edit mode, hidden in view mode)
    { id: 'state_usa', label: 'state_id', minWidth: 100, type: 'state' },
    { id: 'country', label: 'country_id', minWidth: 100, type: 'country' },
    { id: 'industry_tag', label: 'industry_tag_id', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_two', label: 'industry_tag_two_id', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_three', label: 'industry_tag_three_id', minWidth: 150, type: 'industryTag' },
    { id: 'industry_tag_four', label: 'industry_tag_four_id', minWidth: 150, type: 'industryTag' },
    // Additional columns not in user's list (added to end)
    { 
      id: 'type_', 
      label: 'Type', 
      minWidth: 100, 
      type: 'autocomplete',
      options: [
        'THC',
        'CBD',
        'INV',
        'MED',
        'THC, CBD',
        'THC, INV',
        'THC, MED',
        'CBD, INV',
        'CBD, MED',
        'INV, MED',
        'THC, CBD, INV',
        'THC, CBD, MED',
        'CBD, INV, MED',
        'THC, CBD, INV, MED'
      ]
    },
    { id: 'status', label: 'Status', minWidth: 100, type: 'text' },
    // ID column must be last
    { id: 'id', label: 'ID', minWidth: 80, type: 'number' },
  ], []); // Empty dependency array since columns are static

  // ID columns that should be unchecked by default in view mode
  const idColumns = ['state_usa', 'country', 'industry_tag', 
    'industry_tag_two', 'industry_tag_three', 'industry_tag_four'];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => ({ 
      id: col.id, 
      label: col.label, 
      visible: idColumns.includes(col.id) ? false : true 
    }))
  );

  // Load companies data from API
  const loadCompanies = async (page: number = 0, filters?: FilterCriteria[], refresh?: boolean) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use provided filters or fall back to appliedFilters from state
      const filtersToUse = filters !== undefined ? filters : appliedFilters;
      
      let response: CompaniesResponse;
      
      // Use search endpoint if filters are applied, otherwise use regular get endpoint
      if (filtersToUse.length > 0) {
        response = await ApiService.searchCompanies({
          filters: filtersToUse,
          pagination: {
            page: page,
            limit: 100
          },
          sort: sortConfig.column,
          direction: sortConfig.direction,
          refresh
        });
      } else {
        response = await ApiService.getCompanies({
          page: page.toString(),
          limit: 100,
          sort: sortConfig.column,
          direction: sortConfig.direction,
          refresh
        });
      }
      
      // Always replace data (no appending in page-based pagination)
      setCompanies(response.data);
      
      setPagination({
        page: page,
        limit: 100,
        total: response.pagination.total
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load companies');
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCompanies(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // loadCompanies is stable and doesn't need to be in dependencies

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    loadCompanies(newPage, appliedFilters);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination({ page: 0, limit: newLimit, total: pagination.total });
    // For now, we keep limit at 100. If you want to support changing it, add backend support
    loadCompanies(0, appliedFilters);
  };

  const handleColumnToggle = (columnId: string, visible: boolean) => {
    setVisibleColumns(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible } : col
      )
    );
  };

  const handleFiltersApply = (newFilters: FilterCriteria[]) => {
    setAppliedFilters(newFilters);
    setSelectedAllInQuery(false);
    // Reset to page 0 with new filters
    setPagination(prev => ({ ...prev, page: 0 }));
    // Load data with new filters
    loadCompanies(0, newFilters);
  };

  const handleClearFilters = () => {
    setAppliedFilters([]);
    setSelectedAllInQuery(false);
    // Reset to page 0
    setPagination(prev => ({ ...prev, page: 0 }));
    // Reload without filters
    loadCompanies(0, []);
  };

  const handleSendToOutreach = async () => {
    if (!listName.trim()) {
      setOutreachErrorDialog({
        open: true,
        title: 'Campaign Name Required',
        message: 'Please enter a name for your outreach campaign list in the text box first.'
      });
      return;
    }

    if (!isListNameSaved) {
      setOutreachErrorDialog({
        open: true,
        title: 'Save Campaign Name First',
        message: 'Please click the checkmark button next to the list name to save and lock the name before sending to outreach.'
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let domains: string[] = [];

      if (selectedAllInQuery) {
        if (pagination.total === 0) {
          setOutreachErrorDialog({
            open: true,
            title: 'No Results in Query',
            message: 'There are zero results in the active query/selection to send to Outreach.'
          });
          setLoading(false);
          return;
        }
        // Fetch all matching websites for active filters
        const result = await ApiService.getWebsitesByFilter(appliedFilters);
        if (result && result.websites) {
          domains = result.websites;
        }
      } else {
        // Fetch rows selected in the table
        const selectedIds = editMode.selectedRowsRef.current;
        if (selectedIds.size === 0) {
          setOutreachErrorDialog({
            open: true,
            title: 'No Rows Selected',
            message: 'Please select at least one row or check "select all in query" to send domains to Outreach.'
          });
          setLoading(false);
          return;
        }
        domains = companies
          .filter(c => selectedIds.has(c.id) && c.website)
          .map(c => c.website as string);
      }

      // De-duplicate and filter out empty domains
      domains = Array.from(new Set(domains.map(d => d.trim()).filter(Boolean)));

      if (domains.length === 0) {
        setOutreachErrorDialog({
          open: true,
          title: 'No Valid Domains',
          message: 'The selected selection does not contain any valid website domains.'
        });
        setLoading(false);
        return;
      }

      // Post domains to outreach staging
      const response = await ApiService.checkDomainsJson(domains);
      if (response && response.sessionId) {
        const sessionId = response.sessionId;
        
        // Update session list name
        await ApiService.updateSessionName(sessionId, listName.trim());

        // Redirect to outreach process
        navigate(`/outreach/process?session=${sessionId}`);
      } else {
        throw new Error('Failed to initialize outreach staging session.');
      }

    } catch (err) {
      console.error('Error sending to outreach:', err);
      const message = err instanceof Error ? err.message : 'Failed to send to outreach';
      setError(message);
      setOutreachErrorDialog({
        open: true,
        title: 'Send to Outreach Failed',
        message,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSendToSearch = async () => {
    if (!listName.trim()) {
      setOutreachErrorDialog({
        open: true,
        title: 'Campaign Name Required',
        message: 'Please enter a name for your outreach campaign list in the text box first.'
      });
      return;
    }

    if (!isListNameSaved) {
      setOutreachErrorDialog({
        open: true,
        title: 'Save Campaign Name First',
        message: 'Please click the checkmark button next to the list name to save and lock the name before sending to Search.'
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let params: { filters?: FilterCriteria[], companyIds?: number[], name?: string } = {
        name: listName.trim(),
      };

      if (selectedAllInQuery) {
        if (pagination.total === 0) {
          setOutreachErrorDialog({
            open: true,
            title: 'No Results in Query',
            message: 'There are zero results in the active query/selection to send to Search.'
          });
          setLoading(false);
          return;
        }
        params.filters = appliedFilters;
      } else {
        const selectedIds = editMode.selectedRowsRef.current;
        if (selectedIds.size === 0) {
          setOutreachErrorDialog({
            open: true,
            title: 'No Rows Selected',
            message: 'Please select at least one row or check "select all in query" to send to Search.'
          });
          setLoading(false);
          return;
        }
        params.companyIds = Array.from(selectedIds);
      }

      const response = await ApiService.sendToSearch(params);
      if (response && response.success) {
        // Navigate straight to the scoped batch so only these rows load.
        const listParam = response.list_name || listName.trim();
        navigate(`/outreach/search?list=${encodeURIComponent(listParam)}`);
      } else {
        throw new Error('Failed to send to search.');
      }

    } catch (err) {
      console.error('Error sending to search:', err);
      const message = err instanceof Error ? err.message : 'Failed to send to search';
      setError(message);
      setOutreachErrorDialog({
        open: true,
        title: 'Send to Search Failed',
        message,
      });
    } finally {
      setLoading(false);
    }
  };


  // Edit Mode Configuration
  const editModeConfig: EditModeConfig<Company> = {
    apiMethods: {
      create: (data: any) => ApiService.createCompany(data),
      update: (id: number, data: any) => ApiService.updateCompany(id, data),
      delete: (id: number) => ApiService.deleteCompany(id),
    },
    fields: {
      id: { type: 'number', required: false, editable: false },
      name: { type: 'text', required: true, editable: true },
      description: { type: 'text', required: false, editable: true, defaultValue: undefined },
      website: { type: 'text', required: false, editable: true, defaultValue: undefined },
      location: { type: 'text', required: false, editable: true, defaultValue: undefined },
      employees_onlinkedin: { type: 'text', required: false, editable: true, defaultValue: undefined },
      state_usa: { type: 'number', required: false, editable: true, defaultValue: undefined },
      state_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      type_: { type: 'text', required: false, editable: true, defaultValue: undefined },
      license: { type: 'text', required: false, editable: true, defaultValue: undefined },
      country: { type: 'number', required: false, editable: true, defaultValue: undefined },
      country_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_two: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_tag_two_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_three: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_tag_three_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      industry_tag_four: { type: 'number', required: false, editable: true, defaultValue: undefined },
      industry_tag_four_name: { type: 'text', required: false, editable: false, defaultValue: undefined },
      status: { type: 'number', required: true, editable: true, defaultValue: 1 },
      note: { type: 'text', required: false, editable: true, defaultValue: undefined },
      Facebook: { type: 'text', required: false, editable: true, defaultValue: undefined },
      twitter: { type: 'text', required: false, editable: true, defaultValue: undefined },
      ig: { type: 'text', required: false, editable: true, defaultValue: undefined },
      Linkedin: { type: 'text', required: false, editable: true, defaultValue: undefined },
      Youtube: { type: 'text', required: false, editable: true, defaultValue: undefined },
      company_address: { type: 'text', required: false, editable: true, defaultValue: undefined },
      snov_id: { type: 'number', required: false, editable: true, defaultValue: undefined },
      deliverable: { type: 'number', required: false, editable: true, defaultValue: undefined },
      risky: { type: 'number', required: false, editable: true, defaultValue: undefined },
      unknown: { type: 'number', required: false, editable: true, defaultValue: undefined },
      undeliverable: { type: 'number', required: false, editable: true, defaultValue: undefined },
      not_verified: { type: 'number', required: false, editable: true, defaultValue: undefined },
      in_mailer: { type: 'number', required: false, editable: true, defaultValue: undefined },
      unsubscribe: { type: 'number', required: false, editable: true, defaultValue: undefined },
      bounce: { type: 'number', required: false, editable: true, defaultValue: undefined },
      phone: { type: 'text', required: false, editable: true, defaultValue: undefined },
      linkedin_id: { type: 'number', required: false, editable: true, defaultValue: undefined },
      clean_name: { type: 'text', required: false, editable: true, defaultValue: undefined },
      suspicious: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_ran_snov: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_ran_gmo: { type: 'text', required: false, editable: true, defaultValue: undefined },
      last_ran_reoon: { type: 'text', required: false, editable: true, defaultValue: undefined },
    },
    primaryKey: 'id',
    displayName: 'Company',
    validate: (row, isNew) => {
      if (isNew) {
        const nameValue = row.name;
        const trimmedName = nameValue ? String(nameValue).trim() : '';
        if (!trimmedName) {
          return 'Name is required for new rows';
        }
      }
      return null;
    },
    prepareData: (row, originalRow) => {
      // Helper to convert null to undefined for Company type compatibility
      const toUndefined = (value: any) => (value === null ? undefined : value);
      
      // For new rows, ensure all fields are properly set
      if (!originalRow) {
        return {
          name: row.name ? String(row.name).trim() : '',
          description: toUndefined(row.description),
          website: toUndefined(row.website),
          location: toUndefined(row.location),
          employees_onlinkedin: toUndefined(row.employees_onlinkedin),
          state_usa: toUndefined(row.state_usa),
          type_: toUndefined(row.type_),
          license: toUndefined(row.license),
          country: toUndefined(row.country),
          industry_tag: toUndefined(row.industry_tag),
          status: row.status !== undefined ? (typeof row.status === 'number' ? row.status : parseInt(String(row.status)) || 1) : 1,
          note: toUndefined(row.note),
          Facebook: toUndefined(row.Facebook),
          twitter: toUndefined(row.twitter),
          ig: toUndefined(row.ig),
          Linkedin: toUndefined(row.Linkedin),
          Youtube: toUndefined(row.Youtube),
          company_address: toUndefined(row.company_address),
          industry_tag_two: toUndefined(row.industry_tag_two),
          industry_tag_three: toUndefined(row.industry_tag_three),
          industry_tag_four: toUndefined(row.industry_tag_four),
          snov_id: toUndefined(row.snov_id),
          deliverable: toUndefined(row.deliverable),
          risky: toUndefined(row.risky),
          unknown: toUndefined(row.unknown),
          undeliverable: toUndefined(row.undeliverable),
          not_verified: toUndefined(row.not_verified),
          in_mailer: toUndefined(row.in_mailer),
          unsubscribe: toUndefined(row.unsubscribe),
          bounce: toUndefined(row.bounce),
          phone: toUndefined(row.phone),
          linkedin_id: toUndefined(row.linkedin_id),
          clean_name: toUndefined(row.clean_name),
          suspicious: row.suspicious !== undefined ? (typeof row.suspicious === 'string' ? row.suspicious : String(row.suspicious)) : undefined,
          last_ran_snov: toUndefined(row.last_ran_snov),
          last_ran_gmo: toUndefined(row.last_ran_gmo),
          last_ran_reoon: toUndefined(row.last_ran_reoon),
        };
      }
      
      // OPTIMIZATION: For updates, ONLY process fields that are in the row (changed fields)
      // This dramatically reduces processing time when only 1-2 fields are changed
      const updateData: Partial<Company> = {};
      
      // Only process fields that actually changed (exist in row)
      Object.keys(row).forEach(key => {
        const fieldKey = key as keyof Company;
        
        // Handle special cases
        if (fieldKey === 'status') {
          updateData.status = typeof row.status === 'number' 
            ? row.status 
            : parseInt(String(row.status)) || originalRow.status || 1;
        } else if (fieldKey === 'suspicious') {
          updateData.suspicious = row.suspicious !== undefined && row.suspicious !== null 
            ? (typeof row.suspicious === 'string' ? row.suspicious : String(row.suspicious))
            : undefined;
        } else if (fieldKey === 'name') {
          updateData.name = row.name ? String(row.name).trim() : '';
        } else {
          // For all other fields, always include the value (including null)
          // This ensures that clearing a field (setting to null) is properly saved
          const value = (row as any)[fieldKey];
          // Always include the value - null is a valid value that means "clear this field"
          (updateData as any)[fieldKey] = value;
        }
      });
      
      return updateData;
    },
  };

  // Use the edit mode hook
  const editMode = useEditMode(editModeConfig, {
    data: companies,
    onDataChange: setCompanies,
    onRefresh: async () => {
      await loadCompanies(pagination.page, appliedFilters, true);
    },
    onError: (errorMessage) => {
      setError(errorMessage);
    },
  });

  // Filter columns based on visibility and edit mode
  // In edit mode: hide name columns, show ID columns (maintaining order)
  // In view mode: respect visibleColumns state (ID columns unchecked by default but can be shown if selected)
  const columns = useMemo(() => {
    // Map name columns to their corresponding ID columns
    const nameToIdMap: Record<string, string> = {
      'state_name': 'state_usa',
      'country_name': 'country',
      'industry_name': 'industry_tag',
      'industry_tag_two_name': 'industry_tag_two',
      'industry_tag_three_name': 'industry_tag_three',
      'industry_tag_four_name': 'industry_tag_four',
    };
    
    // Build filtered columns maintaining order from allColumns
    const filtered: typeof allColumns = [];
    
    allColumns.forEach(col => {
      const isVisible = visibleColumns.find(vc => vc.id === col.id)?.visible;
      
      if (editMode.isEditMode) {
        // In edit mode: replace name columns with ID columns, hide ID columns that have name equivalents
        if (nameToIdMap[col.id]) {
          // This is a name column - replace with corresponding ID column
          const idColumnId = nameToIdMap[col.id];
          const idColumn = allColumns.find(c => c.id === idColumnId);
          if (idColumn && !filtered.find(c => c.id === idColumnId)) {
            filtered.push(idColumn);
          }
        } else if (!Object.values(nameToIdMap).includes(col.id)) {
          // This is not an ID column that replaces a name column - include if visible
          if (isVisible) {
            filtered.push(col);
          }
        }
        // Skip ID columns that replace name columns (they're already added above)
      } else {
        // In non-edit mode: respect visibleColumns state
        if (isVisible) {
          filtered.push(col);
        }
      }
    });
    
    return filtered;
  }, [allColumns, visibleColumns, editMode.isEditMode]);

  // Use the edit warning hook
  const editWarning = useEditWarning('editWarningShown_global', () => {
    editMode.handleEdit();
  });

  const handleEdit = () => {
    if (editMode.isEditMode) {
      editMode.handleCancel();
      return;
    }
    editWarning.handleEditClick();
  };

  const handleImport = () => {
    setShowImportDialog(true);
  };

  const handleImportComplete = () => {
    // Reload companies after import
    loadCompanies(pagination.page, appliedFilters, true);
  };

  const handleDownload = () => {
    // Show confirmation dialog first
    setShowDownloadConfirmation(true);
  };

  const handleConfirmDownload = async () => {
    setShowDownloadConfirmation(false);
    
    try {
      setLoading(true);
      setError(null);

      // Get filters from state
      const filtersToUse = appliedFilters.length > 0 ? appliedFilters : [];

      // Call export API
      const blob = await ApiService.exportCompanies(filtersToUse);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `companies_export_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error downloading companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to download companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    
    // Map frontend column to backend sort column
    let backendSortColumn = column;
    if (column === 'industry_name') {
      backendSortColumn = 'industry_tag'; // Backend sorts by industry_tag but displays industry_name
    } else if (column === 'industry_tag_two_name') {
      backendSortColumn = 'industry_tag_two';
    } else if (column === 'industry_tag_three_name') {
      backendSortColumn = 'industry_tag_three';
    } else if (column === 'industry_tag_four_name') {
      backendSortColumn = 'industry_tag_four';
    }
    
    // Determine new sort direction
    let newDirection: 'ASC' | 'DESC' = 'ASC';
    if (sortConfig.column === backendSortColumn) {
      // Same column - toggle direction
      newDirection = sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    }
    
    // Update sort config
    const newSortConfig = {
      column: backendSortColumn,
      direction: newDirection
    };
    
    setSortConfig(newSortConfig);
    
    // Reset to page 0 with new sort
    setPagination(prev => ({ ...prev, page: 0 }));
    
    // Reload data with new sort
    setTimeout(() => {
      loadCompanies(0, appliedFilters);
    }, 0);
  };


  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 0.5
      }}>
        
        {error && (
          <Alert severity={getErrorSeverity(error)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CompactFilterPanel
              columns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onFiltersApply={(filters: FilterCriteria[]) => handleFiltersApply(filters)}
              onClearFilters={handleClearFilters}
              onEdit={canEdit ? handleEdit : undefined}
              onImport={handleImport}
              onDownload={canDownload ? handleDownload : undefined}
              onAddNew={() => {}}
              filterType="company"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            )}
            
            {/* Inline Top Pagination Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mr: 0.5 }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
                {pagination.page * 100 + 1}-{Math.min((pagination.page + 1) * 100, pagination.total)} of {pagination.total}
              </span>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  onClick={(e) => handlePageChange(e, pagination.page - 1)}
                  disabled={pagination.page === 0}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                    backgroundColor: pagination.page === 0 ? 'rgba(0, 0, 0, 0.02)' : '#fff',
                    cursor: pagination.page === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.8125rem',
                    opacity: pagination.page === 0 ? 0.4 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    width: '24px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title="Previous Page"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => handlePageChange(e, pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / 100) - 1}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                    backgroundColor: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 'rgba(0, 0, 0, 0.02)' : '#fff',
                    cursor: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.8125rem',
                    opacity: pagination.page >= Math.ceil(pagination.total / 100) - 1 ? 0.4 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '24px',
                    width: '24px',
                    boxShadow: 'none',
                    outline: 'none'
                  }}
                  title="Next Page"
                >
                  ›
                </button>
              </Box>
            </Box>

            <Button
              variant="outlined"
              onClick={() => {
                // Exit edit mode if active
                if (editMode.isEditMode) {
                  editMode.handleCancel();
                }
                // Reload data with cache refresh
                loadCompanies(pagination.page, appliedFilters, true);
              }}
              sx={{ 
                minWidth: '36px', 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                p: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderColor: 'rgba(0, 0, 0, 0.15)',
                color: 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <RefreshIcon sx={{ fontSize: '1.25rem' }} />
            </Button>
          </Box>
        </Box>
        
        {/* Edit Mode Controls - Visible for all users */}
        {canEdit && (
          <EditModeControls
            isEditMode={editMode.isEditMode}
            saving={editMode.saving}
            onSave={editMode.handleSave}
            onCancel={editMode.handleCancel}
            onAddNew={editMode.handleAddNew}
            selectedRowsRef={editMode.selectedRowsRef}
            onDeleteClick={editMode.handleDeleteClick}
            deleteDialogRef={editMode.deleteDialogRef}
            refreshNotificationRef={editMode.refreshNotificationRef}
            listName={listName}
            onListNameChange={(val) => {
              setListName(val);
              setIsListNameSaved(false);
            }}
            isNameSaved={isListNameSaved}
            onSaveListName={() => {
              if (listName.trim()) {
                setIsListNameSaved(true);
              }
            }}
            onSendToOutreach={handleSendToOutreach}
            onSendToSearch={handleSendToSearch}
            selectedAllInQuery={selectedAllInQuery}
            onToggleSelectAllInQuery={() => setSelectedAllInQuery(prev => !prev)}
            totalQueryCount={pagination.total}
          />
        )}
      </Box>
      
      {/* Table Section */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden', // Clip content, scrolling handled by TableContainer
        }}>
          <DataTable
            columns={columns}
            rows={editMode.displayRows}
            page={pagination.page}
            rowsPerPage={100}
            totalRows={pagination.total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            {...({ onSort: handleSort, sortConfig } as any)}
            loading={loading}
            isEditMode={editMode.isEditMode}
            onCellEdit={editMode.handleCellEdit}
            editableColumns={columns.filter(col => {
              // Exclude read-only fields (ID field and name columns)
              // ID columns (state_usa, country, industry_tag, etc.) are editable in edit mode
              const readOnlyFields = ['id', 'state_name', 'country_name', 'industry_name', 
                'industry_tag_two_name', 'industry_tag_three_name', 'industry_tag_four_name'];
              return !readOnlyFields.includes(col.id);
            }).map(col => col.id)}
            isRowSelected={editMode.isRowSelected}
            selectedRowsCount={editMode.selectedRowsCount}
            selectAllVersion={editMode.selectAllVersion}
            onRowSelect={editMode.handleRowSelect}
            onSelectAll={editMode.handleSelectAll}
            savingRows={new Set()}
            stickyColumns={['website', 'clean_name', 'name']}
          />
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        dialogRef={editMode.deleteDialogRef}
        selectedRowsRef={editMode.selectedRowsRef}
        onConfirm={editMode.handleConfirmDelete}
      />

      {/* Refresh Notification Dialog */}
      <RefreshNotificationDialog
        dialogRef={editMode.refreshNotificationRef}
      />

      {/* Edit Warning Dialog */}
      <EditWarningDialog
        open={editWarning.showWarning}
        onClose={editWarning.handleCancel}
        onProceed={editWarning.handleProceed}
      />

      {/* Download Confirmation Dialog */}
      <Dialog open={showDownloadConfirmation} onClose={() => setShowDownloadConfirmation(false)}>
        <DialogTitle>Confirm Download</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to download the company data as a CSV file. Please ensure you handle this data responsibly and do not misuse it. The download will include all company records based on your current filters.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDownloadConfirmation(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmDownload} variant="contained" color="primary">Confirm Download</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog - only show if not managed by global import manager */}
      {showImportDialog && !importManager.state.progress && (
        <ImportDialog
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          type="company"
          onImportComplete={handleImportComplete}
        />
      )}

      {/* Outreach Validation Error Dialog */}
      <Dialog 
        open={outreachErrorDialog.open} 
        onClose={() => setOutreachErrorDialog({ open: false, title: '', message: '' })}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{outreachErrorDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {outreachErrorDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOutreachErrorDialog({ open: false, title: '', message: '' })} 
            color="primary" 
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyView;
